/**
 * Toggleable debug logger for Chrona.
 *
 * Enable:  localStorage.setItem('chrona_debug', 'true')  — or —  ?debug=true in URL
 * Disable: localStorage.removeItem('chrona_debug')        — or —  ?debug=false in URL
 *
 * All output goes to console.debug() so it can be filtered in DevTools.
 */

const STORAGE_KEY = 'chrona_debug';

/** Check URL params on load — ?debug=true persists to localStorage. */
function checkUrlFlag(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('debug');
    if (flag === 'true') localStorage.setItem(STORAGE_KEY, 'true');
    if (flag === 'false') localStorage.removeItem(STORAGE_KEY);
  } catch {
    // SSR or restricted environment — ignore
  }
}
checkUrlFlag();

function isEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const t0 = Date.now();

function ts(): string {
  return `+${((Date.now() - t0) / 1000).toFixed(3)}s`;
}

/** Scoped debug logger — call `createLogger('auth')` to get `[chrona:auth]` prefix. */
export function createLogger(scope: string) {
  const prefix = `[chrona:${scope}]`;
  return {
    log: (...args: unknown[]) => {
      if (isEnabled()) console.debug(prefix, ts(), ...args);
    },
    warn: (...args: unknown[]) => {
      if (isEnabled()) console.warn(prefix, ts(), ...args);
    },
    error: (...args: unknown[]) => {
      // Errors always log, regardless of debug flag
      console.error(prefix, ts(), ...args);
    },
    /** Time an async operation. Returns the result. */
    time: async <T>(label: string, fn: () => Promise<T>): Promise<T> => {
      if (!isEnabled()) return fn();
      const start = performance.now();
      console.debug(prefix, ts(), `${label} — start`);
      try {
        const result = await fn();
        console.debug(prefix, ts(), `${label} — done (${(performance.now() - start).toFixed(0)}ms)`);
        return result;
      } catch (err) {
        console.debug(prefix, ts(), `${label} — FAILED (${(performance.now() - start).toFixed(0)}ms)`, err);
        throw err;
      }
    },
  };
}
